import { faLock, faMicrophone, faVideo } from '@fortawesome/pro-solid-svg-icons';
import { TFunction } from 'i18next';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Icon, cn } from '@growtherapy/sprout-ui';

import { useTheme } from '../hooks';
import { Translate } from '../i18n/Translate';
import { NoAccessVariant } from '../session/controls';
import { Theme } from '../state';

function getDesktopNoAccessMessage(
  t: TFunction,
  noAccessVariant: NoAccessVariant | undefined,
  isSafari: boolean,
): string {
  if (isSafari) {
    switch (noAccessVariant) {
      case NoAccessVariant.Microphone:
        return t('telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourMicNavigateToSafariSettings');
      case NoAccessVariant.Camera:
        return t('telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraNavigateToSafariSettings');
      default:
        return t('telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraAndMicNavigateToSafariSettings');
    }
  }
  switch (noAccessVariant) {
    case NoAccessVariant.Microphone:
      return t('telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourMicSelectSettingsIcon');
    case NoAccessVariant.Camera:
      return t('telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraSelectSettingsIcon');
    default:
      return t('telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraAndMicSelectSettingsIcon');
  }
}

export function BlockedPermissionDescription({
  isMobile,
  isSafari,
  noAccessVariant,
}: {
  isMobile: boolean;
  isSafari: boolean;
  noAccessVariant?: NoAccessVariant;
}) {
  const t = useTranslate();
  const { theme } = useTheme();

  const isDarkTheme = theme === Theme.DARK;
  const mediaIcon = noAccessVariant === NoAccessVariant.Microphone ? faMicrophone : faVideo;

  const iconComponents = {
    1: <Icon icon={mediaIcon} size='xs' aria-hidden />,
    3: <Icon icon={faLock} size='xs' aria-hidden />,
  };

  return isMobile ? (
    <>
      <Body.Default
        className={cn('text-neutral-800 rebrand:text-text-default', {
          'rebrand:text-text-inverse': isDarkTheme,
        })}
        data-testid='blocked-permissions.mobile-instructions-section'
      >
        {noAccessVariant === NoAccessVariant.Microphone ? (
          <Translate
            i18nKey='telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourMicAllowAccessBySelecting'
            components={iconComponents}
          />
        ) : noAccessVariant === NoAccessVariant.Camera ? (
          <Translate
            i18nKey='telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraAllowAccessBySelecting'
            components={iconComponents}
          />
        ) : (
          <Translate
            i18nKey='telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraAndMicAllowAccessBySelecting'
            components={iconComponents}
          />
        )}
      </Body.Default>
      <section data-testid='blocked-permissions.additional-info-section'>
        <Body.Default
          as='h3'
          className={cn('font-medium text-medium', {
            'rebrand:text-text-inverse': isDarkTheme,
          })}
        >
          {t('telehealth-core:permissions.ifThatDidntWork')}
        </Body.Default>
        <ul className='list-disc ml-4'>
          <li>
            <Body.Default
              className={cn('text-medium text-neutral-800 mb-4 rebrand:text-text-default', {
                'rebrand:text-text-inverse': isDarkTheme,
              })}
            >
              <Body.Default
                emphasis
                className={cn('text-lilac-700 font-medium rebrand:text-text-default', {
                  'rebrand:text-text-inverse': isDarkTheme,
                  'rebrand:text-text-default': !isDarkTheme,
                })}
                as='span'
              >
                {t('telehealth-core:permissions.forIphone')}
              </Body.Default>{' '}
              {t('telehealth-core:permissions.openYourDevicesSettingsPrivacyAndSecurity')}
            </Body.Default>
          </li>
          <li>
            <Body.Default
              className={cn('text-medium text-neutral-800 rebrand:text-text-default', {
                'rebrand:text-text-inverse': isDarkTheme,
              })}
            >
              <Body.Default
                emphasis
                className={cn('text-lilac-700 inline font-medium rebrand:text-text-default', {
                  'rebrand:text-text-inverse': isDarkTheme,
                  'rebrand:text-text-default': !isDarkTheme,
                })}
                as='span'
              >
                {t('telehealth-core:permissions.forAndroid')}
              </Body.Default>{' '}
              {t('telehealth-core:permissions.openYourDevicesSettingsSecurityAndPrivacy')}
            </Body.Default>
          </li>
        </ul>
      </section>
    </>
  ) : (
    <>
      <Body.Default
        className={cn('text-medium text-neutral-800 rebrand:text-text-default', {
          'rebrand:text-text-inverse': isDarkTheme,
        })}
        data-testid='blocked-permissions.instructions-section'
      >
        {getDesktopNoAccessMessage(t, noAccessVariant, isSafari)}
      </Body.Default>
    </>
  );
}
